import { useAsanaMCPWorkspaceUsers } from "@/components/EntitySelector/integrations/AsanaMCPSelector/useAsanaMCPWorkspaceUsers";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { AsanaMcpUserEntity, Entity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, User, X } from "lucide-react";
import React, { useMemo, useState } from "react";

interface AsanaUserControlProps extends ControlProps {
  data: AsanaMcpUserEntity | string | undefined;
}

const AsanaUserControlComponent: React.FC<AsanaUserControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  uischema,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("asana");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string"
      ? data
      : data
        ? getEntityId(data as unknown as Entity)
        : null;

  // Get the workspace GID from the form data
  const ctx = useJsonForms();
  const workspaceGid = useMemo(() => {
    // Check if the uischema has a watchField option that specifies the workspace field path
    const watchField = (uischema as any)?.options?.watchField || "workspace";
    const formData = ctx?.core?.data || {};
    const workspaceValue = formData[watchField];
    // Handle both string ID and entity object for workspace
    return typeof workspaceValue === "string"
      ? workspaceValue
      : workspaceValue
        ? getEntityId(workspaceValue as unknown as Entity)
        : "";
  }, [ctx, uischema]);

  // Create a dummy connection to avoid undefined errors in the hook
  const connectionOrDummy =
    integration ||
    ({
      id: "",
      integration_id: "",
    } as any);

  const { data: usersData, isLoading } = useAsanaMCPWorkspaceUsers({
    connection: connectionOrDummy,
    workspaceGid,
    options: {
      enabled: !!integration && !!workspaceGid,
    },
  });

  const users: AsanaMcpUserEntity[] = (usersData || []) as AsanaMcpUserEntity[];

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aName = a.data?.name || "";
      const bName = b.data?.name || "";
      return aName.localeCompare(bName);
    });
  }, [users]);

  const filterFn = (user: AsanaMcpUserEntity) => {
    const name = user.data?.name || "";
    const email = user.data?.email || "";
    const query = searchQuery.toLowerCase();
    return (
      name.toLowerCase().includes(query) || email.toLowerCase().includes(query)
    );
  };

  const renderUserIcon = (user: AsanaMcpUserEntity) => {
    const photo = user.data?.photo?.image_27x27;
    if (photo) {
      return (
        <img
          src={photo}
          alt={user.data?.name || "User"}
          className="w-7 h-7 shrink-0 rounded-full"
        />
      );
    }
    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <User className="w-4! h-4!" />
      </div>
    );
  };

  const renderFn = (user: AsanaMcpUserEntity) => {
    const userId = user.data?.id || "";
    const userName = user.data?.name || "";

    return (
      <CommandItem
        key={userId}
        value={userId}
        onSelect={() => {
          // Return full entity object
          handleChange(path, user);
          setOpen(false);
        }}
      >
        {renderUserIcon(user)}
        <span className="truncate">{userName}</span>
      </CommandItem>
    );
  };

  const selectedUser = useMemo(
    () => users.find((user) => selectedId === user.data?.id),
    [users, selectedId],
  );

  // Disable if no workspace is selected or no integration
  const isDisabled = !enabled || !integration || !workspaceGid;

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={isDisabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedUser ? (
              <div className="flex items-center gap-2 overflow-hidden">
                {renderUserIcon(selectedUser)}
                <span className="truncate">{selectedUser.data?.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {!integration
                  ? "Connect Asana first..."
                  : workspaceGid
                    ? "Select user..."
                    : "Select a workspace first..."}
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedUser && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search users..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? (
                  <Spinner size="md" />
                ) : !integration ? (
                  "Connect Asana first."
                ) : workspaceGid ? (
                  "No users found."
                ) : (
                  "Select a workspace first."
                )}
              </CommandEmpty>
              <CommandGroup>
                {sortedUsers.filter(filterFn).map(renderFn)}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const asanaUserTester = rankWith(5, optionIs("control", "asana_user"));
export const AsanaUserControl = withJsonFormsControlProps(
  AsanaUserControlComponent,
);
